import React, { useContext } from 'react';
import ServiceCard from '../common/ServiceCard';
import { LanguageContext } from '../../App';
import {
  AcademicCapIcon,
  HeartIcon,
  BookOpenIcon,
  CakeIcon,
  CloudIcon,
  UserGroupIcon,
  HomeIcon,
  UserIcon,
  BriefcaseIcon,
  CurrencyRupeeIcon,
} from '@heroicons/react/24/outline';

const translations = {
  en: {
    title: 'Our Services',
    subtitle: 'Various services to help every section of society'
  },
  hi: {
    title: 'हमारी सेवाएं',
    subtitle: 'समाज के हर वर्ग की मदद के लिए हमारी विभिन्न सेवाएं'
  }
};

const services = {
  en: [
    {
      title: 'Child Adoption',
      description: 'Financial assistance for education, health, and basic needs of underprivileged children.',
      icon: <AcademicCapIcon className="w-8 h-8" />,
    },
    {
      title: 'Health Support',
      description: 'Help with medicines, treatments, and surgeries.',
      icon: <HeartIcon className="w-8 h-8" />,
    },
    {
      title: 'Education for All',
      description: 'School supplies, books, and fee assistance for poor children.',
      icon: <BookOpenIcon className="w-8 h-8" />,
    },
    {
      title: 'Food and Nutrition Support',
      description: 'Cooked meals, ration kits, and nutrition assistance.',
      icon: <CakeIcon className="w-8 h-8" />,
    },
    {
      title: 'Disaster Relief Services',
      description: 'Relief during floods, earthquakes, pandemics, etc.',
      icon: <CloudIcon className="w-8 h-8" />,
    },
    {
      title: 'Women Empowerment',
      description: 'Training, skill development, and self-employment for women.',
      icon: <UserGroupIcon className="w-8 h-8" />,
    },
    {
      title: 'Community Development',
      description: 'Clean water, sanitation, education, and health in villages.',
      icon: <HomeIcon className="w-8 h-8" />,
    },
    {
      title: 'Senior Citizen Care',
      description: 'Food, medicines, and dignified care for the elderly.',
      icon: <UserIcon className="w-8 h-8" />,
    },
    {
      title: 'Employment Support',
      description: 'Skill training and livelihood support for youth.',
      icon: <BriefcaseIcon className="w-8 h-8" />,
    },
    {
      title: 'Donation & Fundraising',
      description: 'Continuous online fundraising efforts.',
      icon: <CurrencyRupeeIcon className="w-8 h-8" />,
    },
  ],
  hi: [
    {
      title: 'बच्चों को गोद लेना',
      description: 'वंचित बच्चों की शिक्षा, स्वास्थ्य और बुनियादी जरूरतों के लिए वित्तीय सहायता।',
      icon: <AcademicCapIcon className="w-8 h-8" />,
    },
    {
      title: 'स्वास्थ्य सहायता',
      description: 'दवाइयों, इलाज और सर्जरी के लिए मदद।',
      icon: <HeartIcon className="w-8 h-8" />,
    },
    {
      title: 'सभी के लिए शिक्षा',
      description: 'गरीब बच्चों के लिए स्कूल की सामग्री, किताबें और फीस सहायता।',
      icon: <BookOpenIcon className="w-8 h-8" />,
    },
    {
      title: 'भोजन और पोषण सहायता',
      description: 'पका हुआ खाना, राशन किट और पोषण सहायता।',
      icon: <CakeIcon className="w-8 h-8" />,
    },
    {
      title: 'आपदा राहत सेवाएं',
      description: 'बाढ़, भूकंप, महामारी आदि के दौरान राहत।',
      icon: <CloudIcon className="w-8 h-8" />,
    },
    {
      title: 'महिला सशक्तिकरण',
      description: 'महिलाओं के लिए प्रशिक्षण, कौशल विकास और स्वरोजगार।',
      icon: <UserGroupIcon className="w-8 h-8" />,
    },
    {
      title: 'समुदाय विकास कार्यक्रम',
      description: 'गांवों में स्वच्छ पानी, शौचालय, शिक्षा और स्वास्थ्य।',
      icon: <HomeIcon className="w-8 h-8" />,
    },
    {
      title: 'वरिष्ठ नागरिक सेवा',
      description: 'बुजुर्गों के लिए भोजन, दवाइयां और सम्मानजनक देखभाल।',
      icon: <UserIcon className="w-8 h-8" />,
    },
    {
      title: 'रोज़गार और आजीविका सहायता',
      description: 'युवाओं के लिए कौशल प्रशिक्षण और आजीविका सहायता।',
      icon: <BriefcaseIcon className="w-8 h-8" />,
    },
    {
      title: 'दान एवं फंडरेज़िंग अभियान',
      description: 'निरंतर ऑनलाइन फंडरेज़िंग प्रयास।',
      icon: <CurrencyRupeeIcon className="w-8 h-8" />,
    },
  ]
};

const Services: React.FC = () => {
  const { language } = useContext(LanguageContext);
  const t = translations[language];
  const currentServices = services[language];

  return (
    <section className="bg-gray-50 section-padding">
      <div className="container-custom">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-baloo font-bold text-gray-800 mb-4">
            {t.title}
          </h2>
          <p className="text-gray-600 font-hindi max-w-2xl mx-auto">
            {t.subtitle}
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {currentServices.map((service, index) => (
            <ServiceCard
              key={service.title}
              {...service}
              delay={index * 0.1}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services; 